import type { ReactNode } from 'react';
import { css, keyframes } from 'styled-components';
import styled from 'styled-components';

import { zIndexMap } from '../../../shared/config';
import { TIPS_SHARE_SHEET_CLOSE_ANIMATION_MS } from '../model';

type TipsShareSheetOverlayFrameProps = {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
};

const overlayFadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const overlayFadeOut = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const getOverlayAnimationCss = ({
  isOpen,
  closeAnimationMs,
}: {
  isOpen: boolean;
  closeAnimationMs: number;
}) =>
  isOpen
    ? css`
        animation: ${overlayFadeIn} 300ms ease-out both;
      `
    : css`
        animation: ${overlayFadeOut} ${closeAnimationMs}ms ease-in both;
      `;

const Overlay = styled.div.attrs({
  'data-ui': 'tips-share-sheet-overlay',
})<{ $isOpen: boolean }>`
  /* Intentionally absolute: this sheet is scoped to HomePage.ViewportFrame,
     which owns dynamic viewport height and the mobile frame width cap. */
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-top: ${({ theme }) =>
    `max(${theme.scale.px(65)}, env(safe-area-inset-top))`};
  background: ${({ theme }) => theme.colors.surface.backdrop};
  overscroll-behavior: contain;
  touch-action: pan-y;
  z-index: ${zIndexMap.tipsShare.overlay};
  ${({ $isOpen }) =>
    getOverlayAnimationCss({
      isOpen: $isOpen,
      closeAnimationMs: TIPS_SHARE_SHEET_CLOSE_ANIMATION_MS,
    })};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export function TipsShareSheetOverlayFrame({
  isOpen,
  onClose,
  children,
}: TipsShareSheetOverlayFrameProps) {
  return (
    <Overlay $isOpen={isOpen} onClick={onClose}>
      {children}
    </Overlay>
  );
}
